import { splitSeparator } from 'constants/general';
import React, {
  useState, useCallback, useEffect, useRef,
} from 'react';
import { v4 } from 'uuid';
import { List, Button, Box } from '@mui/material';
import AddBoxIcon from '@mui/icons-material/AddBox';

import { arrayMove } from '@mcn-platform/models';
import BulletItemInput from './BulletItemInput';

type BulletItemsProps = {
  value: string
  setValue: (value: any) => void
  placeholder: string
  disabled: boolean
  minItems?: number
};
type ItemType = {
  id: string
  text: string
};

const BulletPoints = ({
  value,
  setValue,
  placeholder,
  disabled,
  minItems = 0,
}: BulletItemsProps) => {
  // creating array of objects with unique id's so drag&drop will work properly
  const initialValue = value?.split(splitSeparator).map((text: string) => ({ id: v4(), text }));
  const [items, setItems] = useState(initialValue as Array<ItemType>);

  // '-2' for neutral state of item
  // '-1' is when adding new item
  const [selectedID, setSelectedID] = useState('-2' as string);

  const getTextFromItems = (itemsList: ItemType[]) => (
    [...itemsList].reduce((done, curr) => (done ? `${done}${splitSeparator}${curr.text}` : curr.text), '')
  );

  const handleAdd = (text: string) => {
    const newItems = [...items, { id: v4(), text }];
    setItems(newItems);
    setValue(getTextFromItems(newItems));

    // neither editing nor adding item
    setSelectedID('-2');
  };

  const handleEdit = (text: string) => {
    const updatedItems = items.map(
      (item: ItemType) => ((item.id === selectedID) ? ({ id: item.id, text }) : item),
    );
    setItems(updatedItems);
    setValue(getTextFromItems(updatedItems));

    // neither editing nor adding item
    setSelectedID('-2');
  };
  const handleDelete = (id: string) => {
    let updatedItems = items.filter((item: ItemType) => item.id !== id);
    
    // If minItems is set and we would have fewer items, add empty items to reach minItems
    if (minItems > 0 && updatedItems.length < minItems) {
      const itemsToAdd = minItems - updatedItems.length;
      const emptyItems = Array.from({ length: itemsToAdd }, () => ({ id: v4(), text: '' }));
      updatedItems = [...updatedItems, ...emptyItems];
    }
    
    setItems(updatedItems);
    setValue(getTextFromItems(updatedItems));
  };
  const handleCancel = () => setSelectedID('-2');

  const moveItem = useCallback((dragIndex: number, hoverIndex: number) => {
    const updatedItems = arrayMove([...items], dragIndex, hoverIndex);
    setItems(updatedItems);
    setValue(getTextFromItems(updatedItems));
  }, [items, setValue]);

  useEffect(() => {
    // If the bullet list is empty, then set the first item as selected
    // So that the field is already open and user doesnt have to click on Pen(edit) icon
    // Refer MCN-167
    if (items?.length === 1 && items[0].text === '') {
      setSelectedID(items[0].id);
    }
  }, [items]);

  const itemsRef = useRef<string>(
    Array.isArray(items) ? items.map((item) => item.text).join(splitSeparator) : '',
  );

  useEffect(() => {
    const stringValue = typeof value === 'string' ? value : '';

    if (stringValue === itemsRef.current) return;

    let parsedItems: string[];

    try {
      const parsed = JSON.parse(stringValue);
      if (Array.isArray(parsed) && parsed.every((item) => typeof item === 'string')) {
        parsedItems = parsed;
        console.log('useEffect JSON success');
      } else {
        throw new Error('Invalid JSON structure');
      }
    } catch {
      parsedItems = stringValue
        .split(splitSeparator)
        .map((item) => item.trim())
        .filter((item) => !!item);
      console.log('useEffect JSON failure');
    }

    let newItems = parsedItems.map((text) => ({ id: v4(), text }));

    // If minItems is set and array is empty, initialize with empty items
    if (minItems > 0 && newItems.length === 0) {
      newItems = Array.from({ length: minItems }, () => ({ id: v4(), text: '' }));
    }

    setItems(newItems);
    setSelectedID('-2');
    itemsRef.current = stringValue;
  }, [value, minItems]);

  const renderBulletPoint = (item: ItemType, index: number, bulletDisabled: boolean) => {
    // Prevent deleting if we're at minimum items and this item is empty
    // This keeps at least one empty item visible for better UX
    const isAtMinimum = minItems > 0 && items.length <= minItems;
    const canDelete = !(isAtMinimum && !item.text);

    return (
      <BulletItemInput
        key={item.id}
        id={item.id}
        text={item.text}
        index={index}
        placeholder={placeholder}
        disabled={bulletDisabled}
        selectedID={selectedID}
        setSelectedID={setSelectedID}
        onAdd={handleAdd}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onCancel={handleCancel}
        moveItem={moveItem}
        canDelete={canDelete}
      />
    );
  };

  return (
    <Box
      sx={[{
        width: '100%',
        border: '1px solid',
        borderColor: '#e0e0eb',
        borderRadius: 1,
        padding: 2,
      },
      {
        '&:hover': {
          borderColor: '#111111',
        },
      },
      {
        '&:focus': {
          border: '2px solid #1276dd',
        },
      },
      ]}
    >
      <List sx={{ width: '100%' }}>
        {items?.map((item: ItemType, index: number) => (
          renderBulletPoint(item, index, disabled)
        ))}
        {/* when adding new point (input field) */}
        {(selectedID === '-1') && renderBulletPoint({ id: selectedID, text: '' }, -1, false)}
      </List>
      {(selectedID === '-2' || (selectedID !== '-2' && selectedID !== '-1')) && (
        <Box sx={{ textAlign: 'center' }}>
          <Button
            variant="text"
            disabled={disabled || (selectedID !== '-2' && selectedID !== '-1')}
            onClick={() => setSelectedID('-1')}
            startIcon={<AddBoxIcon />}
          >
            Add
          </Button>
        </Box>
      )}
    </Box>
  );
};

BulletPoints.defaultProps = {
  minItems: 0,
};

export default BulletPoints;
